import React, { useState } from "react";
import { leadSource } from "../lib/leadSource";
import axios from "axios";
import { format } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Calendar } from "./ui/calendar";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { CheckCircle2, CalendarDays } from "lucide-react";

const SLOTS = ["8:00 AM", "10:00 AM", "12:00 PM", "2:00 PM", "4:00 PM"];

const KINDS = {
  consultation: { title: "Book a Consultation", noun: "consultation", service: "Consultation Booking", desc: "Pick a day and time that works for you and we'll confirm your consultation." },
  survey: { title: "Book a $79 On-Site Survey", noun: "on-site survey", service: "On-Site Survey Booking", desc: "Pick a day and time for your $79 on-site survey. The full $79 is credited toward your installation when you book." },
};

const BookingModal = ({ open, onClose, kind = "consultation", extraNote, desc }) => {
  const k = KINDS[kind];
  const [date, setDate] = useState();
  const [slot, setSlot] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const reset = () => {
    setDate(undefined); setSlot(""); setForm({ name: "", email: "", phone: "", message: "" }); setSubmitted(false); setLoading(false);
  };
  const handleClose = (v) => { if (!v) { onClose(); setTimeout(reset, 200); } };

  const canSubmit = form.name && form.email && form.phone && date && slot && !loading;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    const when = `${format(date, "EEEE, MMMM d, yyyy")} at ${slot}`;
    const message = `${k.title} request, preferred time: ${when}.${form.message ? ` Notes: ${form.message}` : ""}${extraNote ? `\n${extraNote}` : ""}`;
    try {
      await axios.post(`${API}/quotes`, {
        name: form.name, email: form.email, phone: form.phone,
        service: k.service, message, ...leadSource(),
      });
      setSubmitted(true);
    } catch (err) {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[560px]" data-testid="booking-modal">
        {submitted ? (
          <div className="py-8 text-center" data-testid="booking-success">
            <CheckCircle2 className="w-14 h-14 text-[#3b9dff] mx-auto mb-4" />
            <h3 className="text-xl font-bold text-[#0a1f44] mb-2">Request sent, {form.name.split(" ")[0] || "friend"}!</h3>
            <p className="text-slate-500 text-sm">
              We've got your preferred time of <strong>{date ? format(date, "MMMM d") : ""} at {slot}</strong>. Chris will confirm your {k.noun} by phone or email. Since he's a one-man shop, reserving ahead secures your slot.
            </p>
            <button onClick={() => handleClose(false)} className="mt-6 px-6 py-3 rounded-full bg-[#3b9dff] text-white font-semibold hover:bg-[#2b8ae8] transition-colors" data-testid="booking-close">Close</button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl font-extrabold text-[#0a1f44] flex items-center gap-2">
                <CalendarDays className="w-5 h-5 text-[#3b9dff]" /> {k.title}
              </DialogTitle>
              <DialogDescription>{desc || k.desc}</DialogDescription>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4 mt-1">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="mb-1.5 block">Choose a date</Label>
                  <div className="rounded-xl border border-slate-200 flex justify-center" data-testid="booking-date">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={{ before: new Date() }}
                      classNames={{ day_selected: "bg-[#3b9dff] text-white hover:bg-[#2b8ae8] focus:bg-[#2b8ae8]", day_today: "bg-[#eef4ff] text-[#0a1f44]" }}
                    />
                  </div>
                </div>
                <div>
                  <Label className="mb-1.5 block">Preferred time</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {SLOTS.map((s) => (
                      <button key={s} type="button" onClick={() => setSlot(s)} data-testid={`booking-slot-${s}`}
                        className={`py-2.5 rounded-lg text-sm font-semibold border transition-colors ${slot === s ? "bg-[#3b9dff] border-[#3b9dff] text-white" : "border-slate-200 text-slate-600 hover:border-[#3b9dff]/50"}`}>{s}</button>
                    ))}
                  </div>
                  {date && slot && (
                    <p className="text-xs text-slate-500 mt-3">Selected: <strong className="text-[#0a1f44]">{format(date, "EEE, MMM d")} at {slot}</strong></p>
                  )}
                </div>
              </div>
              <div>
                <Label htmlFor="bname">Full name</Label>
                <Input id="bname" required value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="John Doe" className="mt-1.5" data-testid="booking-name" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="bemail">Email</Label>
                  <Input id="bemail" type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@email.com" className="mt-1.5" data-testid="booking-email" />
                </div>
                <div>
                  <Label htmlFor="bphone">Phone</Label>
                  <Input id="bphone" required value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="(252) 000-0000" className="mt-1.5" data-testid="booking-phone" />
                </div>
              </div>
              <div>
                <Label htmlFor="bmsg">What do you need? (optional)</Label>
                <Textarea id="bmsg" value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Tell us a little about your project..." className="mt-1.5" rows={2} data-testid="booking-message" />
              </div>
              <button type="submit" disabled={!canSubmit} className="w-full py-3.5 rounded-full bg-[#3b9dff] text-white font-semibold hover:bg-[#2b8ae8] transition-colors disabled:opacity-50" data-testid="booking-submit">
                {loading ? "Sending..." : "Request This Time"}
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BookingModal;
